import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

export const Route = createFileRoute("/es/perdida-de-peso-con-medicamentos")({
  head: () => {
    const base = esHead("medical");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("medical"),
              {
                "@type": "MedicalTherapy",
                "@id": `${SITE}/es/perdida-de-peso-con-medicamentos#tratamiento`,
                name: "Programa médico de pérdida de peso con medicamentos GLP-1",
                description:
                  "Tratamiento supervisado por un médico con medicamentos como semaglutida y tirzepatida, acompañado de seguimiento nutricional y conductual.",
                url: `${SITE}/es/perdida-de-peso-con-medicamentos`,
                recognizingAuthority: { "@id": `${SITE}/#organization` },
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsMedical,
});

function EsMedical() {
  return (
    <EsPageShell
      page="medical"
      title="Pérdida de peso con medicamentos en el Norte de Virginia"
      intro="No todas las personas necesitan una operación. Nuestro programa médico usa medicamentos aprobados por la FDA, junto con apoyo nutricional y conductual, para lograr una pérdida de peso significativa bajo supervisión médica."
    >
      <EsSection id="medicamentos" heading="Qué medicamentos usamos">
        <p>
          Los medicamentos más eficaces de hoy pertenecen al grupo de los agonistas del GLP-1 y
          del GIP. Actúan sobre las mismas señales de saciedad que el cuerpo produce de forma
          natural: retrasan el vaciamiento del estómago, reducen el apetito y calman los
          pensamientos constantes sobre la comida.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Semaglutida</strong> (Wegovy, Ozempic): inyección semanal. Wegovy está
            aprobada específicamente para el control del peso.
          </li>
          <li>
            <strong>Tirzepatida</strong> (Zepbound, Mounjaro): inyección semanal que actúa sobre
            dos receptores y suele producir mayor pérdida de peso.
          </li>
          <li>
            <strong>Liraglutida</strong> (Saxenda): inyección diaria, una alternativa cuando la
            cobertura o la tolerancia lo requieren.
          </li>
          <li>
            <strong>Opciones orales</strong> y medicamentos más antiguos, para pacientes que
            prefieren no inyectarse o cuyo seguro no cubre los GLP-1.
          </li>
        </ul>
      </EsSection>

      <EsSection id="guias" heading="Guías detalladas por medicamento">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            {
              to: "/es/semaglutida" as const,
              title: "Semaglutida",
              sub: "Wegovy® y Ozempic®",
              body: "Inyección semanal o pastilla diaria, con cerca del 15 por ciento de pérdida promedio y la mejor evidencia cardiovascular.",
            },
            {
              to: "/es/tirzepatida" as const,
              title: "Tirzepatida",
              sub: "Zepbound® y Mounjaro®",
              body: "Acción doble GIP y GLP-1, la mayor pérdida de peso promedio de cualquier medicamento: hasta el 21 por ciento.",
            },
            {
              to: "/es/medicamentos-orales-para-bajar-de-peso" as const,
              title: "Medicamentos orales",
              sub: "Fentermina, Contrave® y más",
              body: "Alternativas económicas sin inyección para el hambre, los antojos y la absorción de grasa.",
            },
            {
              to: "/es/medicamentos-para-bajar-de-peso" as const,
              title: "Guía completa",
              sub: "Todas las opciones",
              body: "Comparación de inyecciones y pastillas, requisitos, seguridad y cobertura, en una sola página.",
            },
          ].map((c) => (
            <Link
              key={c.to}
              to={c.to}
              className="group block rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl"
            >
              <h3 className="font-serif text-lg text-brand-mid">{c.title}</h3>
              <p className="text-sm text-muted-foreground">{c.sub}</p>
              <p className="mt-2 text-sm text-foreground/80">{c.body}</p>
              <span className="mt-2.5 inline-block text-sm font-medium text-brand-mid">
                Leer más<span aria-hidden="true"> →</span>
              </span>
            </Link>
          ))}
        </div>
      </EsSection>


      <EsSection id="resultados" heading="Qué resultados se pueden esperar">
        <p>
          En los estudios clínicos, la semaglutida produjo una pérdida promedio cercana al 15 por
          ciento del peso corporal y la tirzepatida entre el 18 y el 21 por ciento, en ambos casos
          combinadas con cambios de alimentación y actividad física. Estos resultados dependen de
          la dosis, del tiempo de tratamiento y de la respuesta individual.
        </p>
      </EsSection>

      <EsSection id="efectos" heading="Efectos secundarios y seguridad">
        <p>
          Los efectos más comunes son náuseas, estreñimiento, diarrea y reflujo, sobre todo al
          subir de dosis. Casi siempre mejoran con el tiempo y con un aumento gradual. Existen
          riesgos menos frecuentes pero importantes, como pancreatitis y problemas de la vesícula.
          Estos medicamentos no se deben usar en el embarazo ni en personas con antecedentes
          familiares de carcinoma medular de tiroides.
        </p>
        <p>
          Por eso el tratamiento se hace con seguimiento médico y no con productos comprados por
          internet sin receta ni supervisión.
        </p>
      </EsSection>

      <EsSection id="mantenimiento" heading="Qué pasa si dejo el medicamento">
        <p>
          La obesidad es una enfermedad crónica. Cuando se suspende el medicamento, la mayoría de
          las personas recupera una parte importante del peso perdido, igual que ocurre con la
          presión arterial cuando se suspende su tratamiento. Por eso planificamos desde el inicio
          la fase de mantenimiento: dosis mínima eficaz, hábitos sostenibles y, en algunos casos,
          la conversación sobre una opción quirúrgica más duradera.
        </p>
      </EsSection>

      <EsSection id="siguiente" heading="Cobertura y siguiente paso">
        <p>
          La cobertura de estos medicamentos varía mucho entre planes. Consulte nuestra página de{" "}
          <Link
            to="/es/cobertura-de-seguro"
            className="font-medium text-brand-logo hover:underline"
          >
            cobertura de seguro
          </Link>{" "}
          o compare con las opciones quirúrgicas de{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>{" "}
          y{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="mas-informacion" heading="Más información en español">
        <p className="flex flex-wrap justify-center gap-3 pt-1">
          <Link
            to="/es/quien-califica-para-medicamentos"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            ¿Quién califica?
          </Link>
          <Link
            to="/es/medicamentos-para-bajar-de-peso"
            className="inline-flex items-center rounded-full border border-brand-deep px-5 py-2 text-sm font-medium text-brand-deep hover:bg-brand-soft/60"
          >
            Guía de medicamentos
          </Link>
        </p>
      </EsSection>
    </EsPageShell>
  );
}
